'use client';
import React, { useEffect, useRef } from "react";
import { LanguageType, LanguageDirection } from "@/lib/types";
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import Image from "next/image";

import { debug } from '@/lib/debug';
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP, ScrollTrigger);

interface SlideImageSectionProps {
  language?: LanguageType
  imageSrc: string
  imageAlt: string
  text: string
  onInitialPosition?: (position: number) => void
  aspectRatio?: number
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};


const SlideImageSection: React.FC<SlideImageSectionProps> = ({
  language = "english",
  imageSrc,
  imageAlt,
  text,
  onInitialPosition,
  aspectRatio
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const isLg = useIsLg();
  const isTb = useIsTb();
  const imageRef = useRef<HTMLImageElement>(null);
  const multiplyOverlayRef = useRef<HTMLDivElement>(null);
  const darkenOverlayRef = useRef<HTMLDivElement>(null);
  // debug.log("aspectRatio", aspectRatio);
  // debug.log("slideImageSection rerender");

  useGSAP(() => {
    // debug.log('inside usegsap aspectRatio', aspectRatio);
    const container = containerRef.current;
    const section = sectionRef.current;
    const textElement = container?.querySelector('.slide-text');
    const image = imageRef.current;
    const multiplyOverlay = multiplyOverlayRef.current;
    const darkenOverlay = darkenOverlayRef.current;

    if (container && textElement && section && image) {
      if (!isLg) {
        if(isTb) {
          gsap.set(section, { padding: "0 2.5rem" });
        } else {
          gsap.set(section, { padding: "0 0.5rem" });
        }
      } else {
      }
      // Note: container aspect-ratio / height is driven by CSS classes
      // (aspect-9/16 tb:aspect-5/3 lg:h-screen lg:aspect-auto). We no longer
      // set aspectRatio via GSAP because doing so while React state is still
      // resolving (isLg, useAspectRatio) produced a 0.5+ CLS shift on load.
      gsap.set(container, {
        scale: isLg ? 0.75 : 1
      });

      // Set initial state for text element to match blur mask reveal animation
      gsap.set(textElement, {
        autoAlpha: 0,
        filter: 'blur(30px)',
        y: +100,
        force3D: true,
      });

      // Set initial state for blend mode overlays
      if (multiplyOverlay && darkenOverlay) {
        if (isLg) {
          // Desktop: start at opacity 0, fade in on scroll
          gsap.set(multiplyOverlay, { opacity: 0 });
          gsap.set(darkenOverlay, { opacity: 0 });
        } else {
          // Mobile: set to maximum opacity immediately
          gsap.set(multiplyOverlay, { opacity: 0.7 });
          gsap.set(darkenOverlay, { opacity: 0.2 });
        }
      }

      // ScrollTrigger.refresh();

      const timeline = gsap.timeline()
        .to(container, {
          // scaleY: 1.01, // TODO: on chrome there are 2-3pxs top/bottom missing????????????????
          scaleY: 1.00, // TODO: on chrome there are 2-3pxs top/bottom missing????????????????
          scaleX: 1.00,
          borderRadius: isLg ? "0px" : '',
          outline: isLg ? "none" : '',
          duration: isLg ? 1 : 0.1,
          ease: "none"
        });

      // Start text animation towards the end of container scaling so blend can start after it
      timeline.to(textElement, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 1.0,
        ease: "power2.out",
        force3D: true,
      }, isLg ? 0.85 : undefined); // Start text at 0.7 on desktop (during container scaling)

      // Add blend mode overlay animations only on desktop
      // Start towards the end of container scaling (0.85) and slightly after text fade-in (0.7)
      if (isLg && multiplyOverlay && darkenOverlay) {
        const blendStartPosition = 0.65; // Start at 0.85 (towards end of container scaling) and slightly after text (0.7)

        timeline
          .to(multiplyOverlay, {
            opacity: 0.7,
            duration: 1.0,
            ease: "power2.out",
            force3D: true,
          }, blendStartPosition)
          .to(darkenOverlay, {
            opacity: 0.2,
            duration: 1.0,
            ease: "power2.out",
            force3D: true,
          }, blendStartPosition);
      }

      ScrollTrigger.create({
        trigger: container,
        start: isLg ? "center center" : "70% bottom",
        end: isLg ? "+=150%" : "70% bottom",
        pin: isLg,
        pinSpacing: isLg,
        // pinType "transform" pins via CSS transform instead of the default
        // "fixed" on desktop. Transforms don't trigger layout shifts, so this
        // kills the ~0.56 CLS hits reported at pin activation + release.
        pinType: 'transform',
        scrub: isLg,
        id: 'slide-image-section',
        // markers: true,
        animation: timeline
      });
    }

  }, { dependencies: [isLg], revertOnUpdate: true });

  useEffect(() => {
    if (containerRef.current && onInitialPosition) {
      const rect = containerRef.current?.getBoundingClientRect();
      const bottomY = window.scrollY + rect.top + rect.height;
      onInitialPosition(bottomY);
    }
  }, [aspectRatio, onInitialPosition]);

  return (
    <section
      ref={sectionRef}
      // padding-narrow-x-mobile
      // padding-narrow-x-desktop
      // border
      className="
      w-full
      ">
      <div
        // style={{ aspectRatio: aspectRatio }}
        // aspect-9/16 tb:aspect-5/3
        // border
        className="
          w-full
          flex items-center justify-center
          ">

        <div
          ref={containerRef}
          // Container size is fully CSS-driven so it renders at the correct
          // dimensions on first paint (no React-state-driven aspectRatio inline
          // style, which caused a 0.5+ CLS while hooks resolved).
          //   mobile  (base):  9/16 portrait
          //   tablet  (tb:):   5/3 landscape
          //   desktop (lg:):   full viewport height (equivalent to the old
          //                    window.innerWidth / window.innerHeight ratio)
          className='
            relative
            max-w-[100%]
            w-full
            aspect-9/16 tb:aspect-5/3 lg:aspect-auto lg:h-screen
            lg:scale-75
            overflow-hidden
            border-radius-main
            outline-secondary
            outline-stroke-soft-200
        '>
          <Image
            ref={imageRef}
            src={imageSrc}
            alt={imageAlt}
            fill
            sizes="100vw"
            loading="eager"
            className='
              object-cover
              object-[70%_center] lg:object-center
            '
          />

          {/* Blend Mode Overlays */}
          {/* Multiply blend mode overlay - 70% opacity */}
          <div
            ref={multiplyOverlayRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              backgroundColor: '#917D64',
              mixBlendMode: 'multiply',
              opacity: isLg ? 0 : 0.7
            }}
          />

          {/* Darken blend mode overlay - 20% opacity */}
          <div
            ref={darkenOverlayRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              backgroundColor: '#917D64',
              mixBlendMode: 'darken',
              opacity: isLg ? 0 : 0.2
            }}
          />

          {/* Content */}
          <div
            className="
              absolute
              bottom-[2rem]
              top-[2rem] right-[1rem] left-[1rem]
		          lg:bottom-[2rem]
              lg:top-[2rem] lg:right-[2rem] lg:left-[2rem]
              flex items-center justify-center
	        ">
            <p
              style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
              className={`
                max-w-full
                container-x-small
                text-center
                text-text-reversed
                text-52-to-28--neue-june
                slide-text
            `}>
              {text}
            </p>
          </div>
        </div>
      </div>
    </section>

  )
}

export default SlideImageSection;
